import { useMemo } from 'react';

import { Card, ImageMantine } from '../../../DataDisplay';
import { LogoGBSmallBlue } from '../../../Icons';
import { AspectRatio, Flex, Group, Stack } from '../../../Layout';
import { Text } from '../../../Typography';
import { dotSeparator } from '../../../Utils/stringUtils';
import { DEFAULT_PTA_IMAGE } from '../../../config';
import classes from './MembershipCard.module.css';

//TODO: (Ui) This card needs to be updated so that it can display the correct information in all implementations @acdodd17
type UIMembership = {
  cause: { name?: string; logo_url?: string | null };
  first_name: string;
  last_name: string;
  expires_at: string;
  card?: { front_url: string; back_url: string };
  year?: number;
};
type MembershipCardProps = {
  membership?: UIMembership;
  onClick?: () => void;
  isWalletCard?: boolean;
  direction?: 'row' | 'column';
};

const StatePTAMembershipImageCard = ({
  membership,
  onClick,
  direction,
}: Partial<MembershipCardProps>) => {
  return (
    <Card shadow="md" p={0} onClick={onClick}>
      <Flex direction={direction ?? { base: 'column', sm: 'row' }}>
        <AspectRatio ratio={16 / 9} pos="relative">
          <ImageMantine src={membership?.card?.front_url} />
        </AspectRatio>
        <AspectRatio ratio={16 / 9}>
          <ImageMantine src={membership?.card?.back_url} />
        </AspectRatio>
      </Flex>
    </Card>
  );
};

const WalletMembershipCard = ({ membership, onClick }: Partial<MembershipCardProps>) => {
  // state PTA determine bg color and Img src
  // non-State PTA, determine bg color and use membership.cause.logo as Img src
  // Empty state (no membership details), bg and fallback img src

  const membershipYear = useMemo(() => {
    if (!membership?.year) return null;
    return `${membership?.year - 1} - ${membership?.year}`;
  }, [membership?.year]);

  const title = useMemo(() => {
    if (membershipYear) {
      return `Membership Card ${dotSeparator} ${membershipYear}`;
    }
    return `Membership Card`;
  }, [membershipYear]);

  const backgroundColor = useMemo(() => {
    if (!membership) {
      return 'cobalt';
    }

    if (!membership?.card) {
      return 'gray';
    }

    return '#00477F';
  }, [membership]);

  const image = useMemo(() => {
    if (!membership) {
      return (
        <Card bg="white" radius="sm" p={7} h={36} w={36} style={{ justifyContent: 'center' }}>
          <LogoGBSmallBlue />
        </Card>
      );
    }

    if (!membership?.card) {
      return (
        <ImageMantine
          className={classes.MembershipCard__walletCardImage}
          src={membership.cause.logo_url}
        />
      );
    }

    return (
      <ImageMantine className={classes.MembershipCard__walletCardImage} src={DEFAULT_PTA_IMAGE} />
    );
  }, [membership]);

  return (
    <Card mih={96} bg={backgroundColor} onClick={onClick} className={classes.defaultMembershipCard}>
      <Group gap="sm" pb="sm" align="flex-start" wrap="nowrap">
        {image}
        <Stack gap={0} c="white" h="100%">
          <Text fz={10} fw={600} ta="left">
            {title}
          </Text>
          <Text ta="left" tt="capitalize">
            {membership?.cause?.name}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
};

const MembershipCard = ({
  membership,
  onClick,
  isWalletCard = false,
  direction,
}: MembershipCardProps) => {
  return membership?.card && !isWalletCard ? (
    <StatePTAMembershipImageCard membership={membership} onClick={onClick} direction={direction} />
  ) : (
    <WalletMembershipCard membership={membership} onClick={onClick} />
  );
};

export { MembershipCard };
export type { UIMembership };
